/**
 * Bar chart impilato, CSS puro e nessuna dipendenza esterna.
 *
 * Sostituisce `BarChartSimple`, che faceva la stessa cosa senza segmenti: una
 * barra con UN segmento è identica alla barra piena di prima, quindi tenere
 * due componenti avrebbe voluto dire mantenere due volte lo stesso layout.
 *
 * I SEGMENTI SPIEGANO IL TOTALE, NON LO CORREGGONO: l'altezza della barra è
 * `totale`, che arriva dal backend insieme ai segmenti e ne è la somma
 * (invariante asserita lato backend). Qui NON si ricalcola sommando i
 * segmenti: se un giorno divergessero, il grafico deve mostrare la stessa
 * cifra della testata, non una terza.
 *
 * Il colore di un segmento è una sfumatura del colore della serie, non un
 * colore nuovo: la card resta riconoscibile («Ingresso è verde») e le fette si
 * distinguono fra loro. La sfumatura è decisa dalla CHIAVE, non dalla
 * posizione, così lo stesso materiale tiene lo stesso tono in tutti i bucket —
 * altrimenti cambierebbe tinta a ogni barra in cui entra o esce qualcosa.
 */
interface Segmento {
  chiave: string;
  etichetta: string;
  valore: number;
}

export interface BarraImpilata {
  /** Testo mostrato sull'asse: e' una RESA, non una chiave. */
  label: string;
  /** Chiave del bucket come l'ha prodotta il backend, da rimandare indietro
   *  (drill-down). Separata dalla label perche' due bucket possono rendersi
   *  con lo stesso testo e risalire dalla resa sceglierebbe il primo. */
  periodo: string;
  totale: number;
  segmenti: Segmento[];
}

interface Props {
  title: string;
  data: BarraImpilata[];
  /** Colore base della serie; i segmenti ne sono sfumature. */
  color: string;
  unit?: string;
  /**
   * Formattatore dei valori. OBBLIGATORIO e senza default: un
   * `toLocaleString('it-IT')` qui dentro ricadrebbe nella trappola nota —
   * CLDR it-IT ha `minimumGroupingDigits: 2`, quindi 8650 resterebbe "8650"
   * mentre 20000 diventa "20.000", due regole nella stessa serie. I kg
   * passano da `fmtKg`, che e' l'unico formattatore di pesi.
   */
  format: (n: number) => string;
  /**
   * Testo dello stato vuoto, GIÀ tradotto. Arriva dal chiamante e non da un
   * `t()` qui dentro: il componente resta senza catalogo, e il check-i18n non
   * ha una stringa italiana letterale da segnalare.
   */
  emptyLabel: string;
  /**
   * Click su una FETTA. Quando c'e', il segmento diventa un `<button>`: cursore,
   * aria-label e raggiungibilita' da tastiera. Assente (il caso normale) resta
   * un `<div>` inerte — un bottone che non fa nulla e' peggio di un rettangolo.
   */
  onSegmentClick?: (barra: BarraImpilata, segmento: Segmento) => void;
  /** aria-label della fetta cliccabile, gia' tradotta dal chiamante. */
  segmentAriaLabel?: (barra: BarraImpilata, segmento: Segmento) => string;
  testid?: string;
}

/** Opacità del segmento: piena per il primo tono, giù fino a 0.45 sull'ultimo. */
function opacita(indice: number, quanti: number): number {
  if (quanti <= 1) return 1;
  return 1 - (0.55 * indice) / (quanti - 1);
}

export default function StackedBarChart({
  title, data, color, unit = 'kg', format, emptyLabel,
  onSegmentClick, segmentAriaLabel, testid = 'barchart-bars',
}: Props) {
  const max = Math.max(0, ...data.map((d) => d.totale));
  const totale = data.reduce((a, d) => a + d.totale, 0);
  // Ordine STABILE delle chiavi su tutto il grafico: decide i toni, e
  // dipendere dall'ordine dentro il singolo bucket li farebbe ballare.
  const chiavi = [...new Set(data.flatMap((d) => d.segmenti.map((s) => s.chiave)))].sort();
  const tono = (chiave: string) => opacita(Math.max(0, chiavi.indexOf(chiave)), chiavi.length);
  // Con poche barre il valore sta sempre sopra la barra; con molte solo a
  // hover (altrimenti le etichette si accavallano e diventano illeggibili).
  const valoriSempreVisibili = data.length <= 8;

  return (
    <div className="bg-white border border-stone-mid rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-dark">{title}</h3>
        <span className="text-xs text-ink-soft">{format(totale)} {unit}</span>
      </div>
      {data.length === 0 || totale === 0 ? (
        <div className="text-xs text-ink-faint py-6 text-center border border-dashed border-stone-border rounded-lg"
          data-testid={`${testid}-vuoto`}>
          {emptyLabel}
        </div>
      ) : (
        <div>
          {/* Area barre: altezza fissa e `items-stretch` → le colonne ereditano
              un'altezza DEFINITA, così la % della barra ha un riferimento reale.
              pt-4 lascia spazio al valore mostrato sopra la barra più alta. */}
          <div className="flex items-stretch gap-1.5 h-40 pt-4" data-testid={testid}>
            {data.map((d, i) => {
              const pct = max > 0 ? (d.totale / max) * 100 : 0;
              // Senza segmenti la barra resta piena: è il caso di una serie che
              // il backend non scompone, non un dato mancante.
              const segmenti = d.segmenti.length > 0
                ? d.segmenti
                : [{ chiave: '__tutto__', etichetta: title, valore: d.totale }];
              return (
                <div
                  key={`${d.label}-${i}`}
                  className="relative flex-1 min-w-0 flex flex-col justify-end group"
                  title={`${d.label}: ${format(d.totale)} ${unit}`}
                >
                  <div className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                    style={{ height: `${pct}%`, minHeight: d.totale > 0 ? 3 : 0 }}>
                    {segmenti.map((s, j) => {
                      // L'altezza della fetta è la sua quota DEL TOTALE della
                      // barra, non del massimo della serie: la barra è già
                      // scalata, le fette la dividono.
                      const stile = {
                        height: d.totale > 0 ? `${(s.valore / d.totale) * 100}%` : '0%',
                        background: color,
                        opacity: tono(s.chiave),
                      };
                      // La `key` NON sta qui dentro: spread su JSX fa scattare
                      // «A props object containing a key prop is being spread
                      // into JSX». React la estrae lo stesso, quindi le fette
                      // erano chiavate correttamente, ma il warning usciva su
                      // stderr a ogni render della Dashboard — e un warning che
                      // compare sempre è il posto dove uno vero si perde.
                      const comune = {
                        'data-testid': `seg-${d.label}-${s.chiave}`,
                        style: stile,
                        title: `${s.etichetta}: ${format(s.valore)} ${unit}`,
                      };
                      const chiave = `${s.chiave}-${j}`;
                      if (!onSegmentClick || s.chiave === '__tutto__') {
                        return <div key={chiave} {...comune} />;
                      }
                      return (
                        <button
                          key={chiave}
                          {...comune}
                          type="button"
                          className="block w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-sage"
                          aria-label={segmentAriaLabel?.(d, s) ?? `${s.etichetta}: ${format(s.valore)} ${unit}`}
                          onClick={() => onSegmentClick(d, s)}
                        />
                      );
                    })}
                  </div>
                  {d.totale > 0 && (
                    <span
                      className={`absolute -top-4 left-0 right-0 text-[9px] text-ink-soft text-center truncate transition-opacity ${
                        valoriSempreVisibili ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {format(d.totale)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {/* Etichette periodo su riga separata: non rubano altezza alle barre. */}
          <div className="flex gap-1.5 mt-1">
            {data.map((d, i) => (
              <span
                key={`lbl-${d.label}-${i}`}
                className="flex-1 min-w-0 text-[9px] text-ink-soft text-center truncate"
              >
                {d.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
